import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import { useLiveUpdatesConnection } from '@/hooks/useLiveUpdates'
import type { LiveUpdatesValue } from '@/hooks/useLiveUpdates'

const LiveUpdatesContext = createContext<LiveUpdatesValue>({
  connected: false,
  presence: null,
  recentlyChanged: new Set(),
})

/**
 * Single app-wide websocket connection for live MCP updates. Mounts inside
 * AuthProvider (needs the token). The "AI agent connected" pill that consumes
 * this context lives in the Navbar (AgentStatusPill).
 */
export function LiveUpdatesProvider({ children }: { children: ReactNode }) {
  const value = useLiveUpdatesConnection()
  return (
    <LiveUpdatesContext.Provider value={value}>
      {children}
    </LiveUpdatesContext.Provider>
  )
}

export function useLiveUpdatesState(): LiveUpdatesValue {
  return useContext(LiveUpdatesContext)
}

/** True while the given object changed in the last few seconds (for highlight). */
export function useIsRecentlyChanged(resourceType: string, id?: string | number | null): boolean {
  const { recentlyChanged } = useContext(LiveUpdatesContext)
  if (id === undefined || id === null) return false
  return recentlyChanged.has(`${resourceType}:${id}`)
}
